import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { DynamicContextProvider, getAuthToken } from '@dynamic-labs/sdk-react-core';
import { EthereumWalletConnectors } from '@dynamic-labs/ethereum';
import { SolanaWalletConnectors } from '@dynamic-labs/solana';
import PasswordGate from './components/PasswordGate';
import { onAuthStateChanged, signOut, signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './services/firebase';
import { toast } from 'sonner';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import Wallet from './pages/Wallet';
import Transfer from './pages/Transfer';
import Convert from './pages/Convert';
import Cards from './pages/Cards';
import CardDetails from './pages/CardDetails';
import Send from './pages/Send';
import BankTransfer from './pages/BankTransfer';
import Transactions from './pages/Transactions';
import Profile from './pages/Profile';
import PaymentSuccess from './pages/PaymentSuccess';
import KYC from './pages/KYC';
import { Toaster } from './components/ui/sonner';

// ── Error Boundary ──
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App ErrorBoundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, color: '#fff', background: '#030014', minHeight: '100vh' }}>
          <h1 style={{ color: '#f88' }}>Something went wrong</h1>
          <pre style={{ color: '#faa', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {this.state.error?.message || 'Unknown error'}
          </pre>
          <pre style={{ color: '#888', whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: 10, fontSize: 12 }}>
            {this.state.error?.stack || ''}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: 20, padding: '10px 20px', background: '#7000FF', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const LoadingFallback = () => (
  <div className="min-h-screen bg-[#030014] flex items-center justify-center">
    <div className="text-white">Loading...</div>
  </div>
);

// ── Bridge: try once when token found, then back off on failure ──
let _bridging = false;
let _bridgeNextRetry = 0; // timestamp — don't retry before this
// Track whether logout was user-initiated (vs Dynamic SDK auto-logout)
let _userInitiatedLogout = false;
// Expose for Layout.js logout button to call before signing out
window.__cryppysLogout = () => { _userInitiatedLogout = true; };

async function tryBridge() {
  if (_bridging || auth.currentUser) return;
  if (Date.now() < _bridgeNextRetry) return; // back-off period

  let token;
  try { token = getAuthToken(); } catch (e) { /* SDK not ready */ }
  if (!token) return;

  _bridging = true;
  console.log('[Bridge] Dynamic token found, calling dynamic_auth_bridge...');

  try {
    const bridgeFn = httpsCallable(functions, 'dynamic_auth_bridge');
    const result = await bridgeFn({ dynamic_token: token, full_name: 'User' });
    await signInWithCustomToken(auth, result.data.firebase_token);
    console.log('[Bridge] Firebase auth SUCCESS! isNewUser:', result.data.is_new_user);
    toast.success(result.data.is_new_user ? 'Account created!' : 'Welcome back!');
  } catch (error) {
    console.error('[Bridge] FAILED:', error?.message || error);
    // Back off: wait 15 seconds before retrying to avoid rate limits
    _bridgeNextRetry = Date.now() + 15000;
    _bridging = false;
  }
}


function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Firebase auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('[App] Firebase auth state:', user ? user.uid : 'null');
      setIsAuthenticated(!!user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Poll for Dynamic token — runs outside any Dynamic context
  useEffect(() => {
    console.log('[App] Starting token poll...');
    // Check immediately
    tryBridge();
    // Then every 1.5 seconds
    const interval = setInterval(tryBridge, 1500);
    return () => clearInterval(interval);
  }, []);

  // Event-based bridge (fires on fresh login only, not session restore)
  const handleDynamicAuthSuccess = async (args) => {
    console.log('[onAuthSuccess] Fired! hasToken:', !!args?.authToken, 'hasUser:', !!args?.user);
    if (args?.authToken && !auth.currentUser) {
      _bridging = true;
      try {
        const bridgeFn = httpsCallable(functions, 'dynamic_auth_bridge');
        const result = await bridgeFn({
          dynamic_token: args.authToken,
          full_name: args.user?.firstName
            ? `${args.user.firstName} ${args.user.lastName || ''}`.trim()
            : args.user?.alias || 'User',
        });
        await signInWithCustomToken(auth, result.data.firebase_token);
        console.log('[onAuthSuccess] Bridge SUCCESS!');
        toast.success(result.data.is_new_user ? 'Account created!' : 'Welcome back!');
      } catch (error) {
        console.error('[onAuthSuccess] Bridge failed:', error);
        _bridging = false;
      }
    }
  };

  const ProtectedRoute = ({ children }) => {
    if (loading) return <LoadingFallback />;
    return isAuthenticated ? children : <Navigate to="/auth" />;
  };

  const PublicRoute = ({ children }) => {
    if (loading) return <LoadingFallback />;
    return !isAuthenticated ? children : <Navigate to="/dashboard" />;
  };

  return (
    <ErrorBoundary>
      <PasswordGate>
        <DynamicContextProvider
          settings={{
            environmentId: process.env.REACT_APP_DYNAMIC_ENVIRONMENT_ID,
            walletConnectors: [EthereumWalletConnectors, SolanaWalletConnectors],
            events: {
              onAuthSuccess: handleDynamicAuthSuccess,
              onLogout: () => {
                console.log('[onLogout] Dynamic logout fired, userInitiated:', _userInitiatedLogout);
                // Only sign out of Firebase if the user explicitly clicked logout
                // Dynamic SDK may auto-logout due to "wallet info out of sync" etc.
                // which should NOT destroy our Firebase session
                if (_userInitiatedLogout) {
                  _userInitiatedLogout = false;
                  signOut(auth).catch(() => {});
                }
              },
            },
          }}
        >
          <Router>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
              <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
              <Route path="/send" element={<ProtectedRoute><Send /></ProtectedRoute>} />
              <Route path="/transfer" element={<ProtectedRoute><Transfer /></ProtectedRoute>} />
              <Route path="/convert" element={<ProtectedRoute><Convert /></ProtectedRoute>} />
              <Route path="/cards" element={<ProtectedRoute><Cards /></ProtectedRoute>} />
              <Route path="/card/:cardId" element={<ProtectedRoute><CardDetails /></ProtectedRoute>} />
              <Route path="/bank-transfer" element={<ProtectedRoute><BankTransfer /></ProtectedRoute>} />
              <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/kyc" element={<ProtectedRoute><KYC /></ProtectedRoute>} />
              <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            </Routes>
          </Router>
        </DynamicContextProvider>
      </PasswordGate>
    </ErrorBoundary>
  );
}

export default App;
